import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Text } from "@mantine/core";
import { useSelector } from "react-redux";
import moment from "moment";
import swal from "sweetalert";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { showError } from "../../utils";
import {
  deleteProviderDocument,
  deleteProviderFolder,
  getProviderDocuments,
  getProviderFolders,
} from "../../services/providerDocuments";
import DocumentsDrive from "../DocumentsDrive";
import RecordNotFound from "../RecordNotFound";
import ProviderFolderModal from "./ProviderFolderModal";
import ProviderPreviewFileModal from "./ProviderPreviewFileModal";
import ProviderRenameFileModal from "./ProviderRenameFileModal";
import SkypointUploadStaffDocDashboard from "../SkypointStaffDetails/SkypointUploadStaffDocDashboard";
import store from "../../store";
import { setUploadDocModal } from "../../store/reducers/document";
import useStyles from "./style";

const idOf = (item) => item?._id || item?.id;

// The nightly cron flips `expired`, but a document whose date has just passed
// should read as expired straight away rather than after the next run.
const isExpired = (doc) =>
  !!doc?.expired || (!!doc?.expiry && moment(doc.expiry).endOf("day").isBefore(moment()));

// A plain <a download> is ignored by browsers for cross-origin (S3) URLs,
// which just opens the file in a new tab instead of downloading it. Fetch
// the bytes and download via a blob URL instead.
const downloadFile = async (doc) => {
  try {
    const response = await fetch(doc.fileUrl);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = doc.name;
    link.click();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    showError("Could not download the file");
  }
};

/**
 * A provider's documents, on the Documents module drive: one breadcrumb trail
 * over folders and files, with the file cards previewing their own first page.
 *
 * The listing itself lives in the reusable DocumentsDrive component; everything
 * here is the provider-specific wiring — fetching, the folder path, and the
 * create / rename / delete / upload modals.
 *
 * @param {Object}   props
 * @param {String}   props.providerId
 * @param {String}   [props.providerName] Root breadcrumb label.
 * @param {Boolean}  props.canManage      False for staff, who get a read-only drive.
 * @param {String}   [props.initialView]  "expired" when arriving from Pending Actions.
 */
const ProviderDocuments = ({ providerId, providerName, canManage, initialView }) => {
  const { classes } = useStyles();
  const [documents, setDocuments] = useState([]);
  const [rootFolders, setRootFolders] = useState([]);
  // Subfolders of the folder currently open, returned alongside its documents.
  const [subFolders, setSubFolders] = useState([]);
  // Folders opened below the root, outermost first. Empty means the provider
  // root. Holding the whole chain rather than just the current folder is what
  // makes every ancestor crumb clickable without another round trip.
  const [path, setPath] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [folderModal, setFolderModal] = useState(null); // { folder?, parent? }
  const [previewFile, setPreviewFile] = useState(null);
  const [renameFile, setRenameFile] = useState(null);

  const currentFolder = path[path.length - 1] || null;

  // Whether the shared upload modal is showing. It reads its own open flag from
  // Redux, so the two are set together.
  const [uploadModal, setUploadModal] = useState(false);
  const openUpload = () => {
    setUploadModal(true);
    store.dispatch(setUploadDocModal(true));
  };
  // The shared modal closes itself through Redux (Cancel, the X, or a save), so
  // the local config has to follow or a stale replace target would linger.
  const { uploadDocModal } = useSelector((state) => state.document);
  const { providerSettings } = useSelector((state) => state.skypointProviders);
  const allowedFileTypes = providerSettings?.allowedFileTypes;
  // Settings › Documents › "Track document expiry". With it off, expiry stops
  // being flagged on cards and rows — there are no Active / Expired tabs left
  // for it to hide.
  const trackDocumentExpiry = providerSettings?.trackDocumentExpiry !== false;

  useEffect(() => {
    if (!uploadDocModal) setUploadModal(false);
  }, [uploadDocModal]);

  const loadAll = useCallback(
    async (folderId) => {
      setLoading(true);
      const [folders, { documents: docs, folderInfo }] = await Promise.all([
        getProviderFolders(providerId),
        getProviderDocuments(providerId, folderId),
      ]);
      setRootFolders(folders);
      setDocuments(docs);
      setSubFolders(folderInfo?.folders || []);
      setLoading(false);
    },
    [providerId]
  );

  useEffect(() => {
    if (providerId) loadAll(idOf(currentFolder));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, currentFolder?._id, currentFolder?.id]);

  const matchesSearch = useCallback(
    (name) => !searchTerm.trim() || name?.toLowerCase().includes(searchTerm.trim().toLowerCase()),
    [searchTerm]
  );

  const visibleFolders = useMemo(
    () => (currentFolder ? subFolders : rootFolders).filter((f) => matchesSearch(f?.name)),
    [currentFolder, subFolders, rootFolders, matchesSearch]
  );

  const visibleFiles = useMemo(() => {
    // Without a folder the endpoint returns every document the provider has,
    // filed or not. In a drive the root shows only what is not inside a folder —
    // anything else would appear twice, once here and once in its folder.
    const inScope = currentFolder ? documents : documents.filter((doc) => !doc?.folder);
    const matching = inScope.filter((doc) => matchesSearch(doc?.name));

    // Pending Actions links here to chase an expiry, so when that is where the
    // user came from the expired documents are floated to the top. There are no
    // Active / Expired tabs any more — expiry reads off the card itself.
    if (initialView === "expired") {
      return [...matching].sort((a, b) => Number(isExpired(b)) - Number(isExpired(a)));
    }
    return matching;
  }, [currentFolder, documents, matchesSearch, initialView]);

  /* ---------------- navigation ---------------- */

  const openFolder = (folder) => setPath((prev) => [...prev, folder]);

  // 0 is the root crumb; every other index is that many folders deep.
  const navigateTo = (index) => setPath((prev) => prev.slice(0, index));

  /* ---------------- actions ---------------- */

  const handleDeleteFile = async (doc) => {
    const confirmed = await swal({
      title: "Delete document?",
      text: `This will permanently remove "${doc.name}". This action cannot be undone.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (!confirmed) return;

    const success = await deleteProviderDocument(providerId, idOf(doc));
    if (success) loadAll(idOf(currentFolder));
  };

  const handleDeleteFolder = async (folder) => {
    const confirmed = await swal({
      title: "Delete folder?",
      text: `This will remove "${folder.name}" and any folders inside it. Documents it holds are kept and moved back to the provider root.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (!confirmed) return;

    const success = await deleteProviderFolder(providerId, idOf(folder));
    if (!success) return;

    // Deleting the folder you are standing in — or any of its ancestors — has
    // to drop you back to its parent; changing the path refetches on its own.
    const depth = path.findIndex((f) => idOf(f) === idOf(folder));
    if (depth > -1) setPath(path.slice(0, depth));
    else loadAll(idOf(currentFolder));
  };

  // Zips a folder's documents client-side, same as the staff folder download.
  const handleDownloadFolder = async (folder) => {
    const files = folder?.files || [];
    if (files.length === 0) {
      swal({ title: "This folder is empty", icon: "info", dangerMode: false });
      return;
    }

    try {
      const zip = new JSZip();
      await Promise.all(
        files.map(async (file) => {
          const response = await fetch(file.fileUrl);
          zip.file(file.name, await response.blob(), { binary: true });
        })
      );
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${folder.name}.zip`);
    } catch (error) {
      showError("Could not download the folder");
    }
  };

  const emptyState = (
    <Box>
      <RecordNotFound />
      <Text ta="center" fz={14} color="#667085" ff="Inter" mt={-16} pb={24}>
        {searchTerm
          ? "No files or folders match your search."
          : canManage
          ? "This folder is empty — use Upload files to add documents."
          : "This folder is empty."}
      </Text>
    </Box>
  );

  return (
    <Box className={classes.wrapper}>
      <DocumentsDrive
        rootName={providerName || "Documents"}
        path={path}
        folders={visibleFolders}
        files={visibleFiles}
        loading={loading}
        search={searchTerm}
        canManage={canManage}
        showExpiry={trackDocumentExpiry}
        onSearchChange={setSearchTerm}
        onNavigate={navigateTo}
        onOpenFolder={openFolder}
        onOpenFile={setPreviewFile}
        onNewFolder={() => setFolderModal({ parent: currentFolder })}
        onUpload={openUpload}
        onDownloadFolder={handleDownloadFolder}
        onRenameFolder={(folder) => setFolderModal({ folder })}
        onDeleteFolder={handleDeleteFolder}
        onDownloadFile={downloadFile}
        onRenameFile={setRenameFile}
        onDeleteFile={handleDeleteFile}
        emptyState={emptyState}
      />

      <ProviderFolderModal
        opened={!!folderModal}
        onClose={() => setFolderModal(null)}
        providerId={providerId}
        data={folderModal?.folder}
        parentFolder={folderModal?.parent}
        onSaved={() => loadAll(idOf(currentFolder))}
      />

      <ProviderPreviewFileModal
        opened={!!previewFile}
        onClose={() => setPreviewFile(null)}
        data={previewFile}
      />

      <ProviderRenameFileModal
        opened={!!renameFile}
        onClose={() => setRenameFile(null)}
        providerId={providerId}
        data={renameFile}
        onSaved={() => loadAll(idOf(currentFolder))}
      />

      {/* The shared upload/preview modal — the same one the dashboard and
          notifications use. */}
      {uploadModal && (
        <SkypointUploadStaffDocDashboard
          providerId={providerId}
          selectedFolder={currentFolder}
          allowedFileTypes={allowedFileTypes}
          onResolved={() => {
            setUploadModal(false);
            loadAll(idOf(currentFolder));
          }}
        />
      )}
    </Box>
  );
};

export default ProviderDocuments;
